import { Alert, Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';

/** Which non-self-serviceable state brought the subscriber here. */
export type SupportVariant = 'admin_block' | 'disabled';

/**
 * Contact-support screen — the `admin_block` and `disabled` branches of the portal
 * flow. Both are states the subscriber cannot clear himself (unlike a financial
 * block, which he can pay off): an operator-set administrative block, or an account
 * switched off after it had been activated.
 *
 * The device has already been registered by the time this screen shows, and saying
 * so is the whole point of the screen's second half: without it a subscriber who
 * sees "blocked" assumes the registration failed and re-registers in a loop.
 *
 * ⚠ «HAS ALREADY BEEN REGISTERED» IS NO LONGER UNCONDITIONAL (D408, production
 * 2026-09-01). The flow now routes a blocked subscriber here on the state the login
 * reported even when `POST /captive/register` was REFUSED — which is the whole point
 * of that change, since the refusal («не найдена активная сессия захвата») is advice
 * he cannot act on while an operator holds the block. So the second half takes
 * `registrationConfirmed` and says the honest thing when nothing was bound; the
 * sentence above still describes the ordinary path, which is why it is kept.
 *
 * The administrative-block sentence is the customer's dictated wording — do not
 * reword it.
 */
export function SupportScreen({
  variant,
  registrationConfirmed = true,
  onRelogin,
}: {
  variant: SupportVariant;
  /** False when this screen was reached from a FAILED registration (D408). */
  registrationConfirmed?: boolean;
  onRelogin: () => void;
}) {
  const { t } = useTranslation();
  const admin = variant === 'admin_block';
  return (
    <Result
      status="warning"
      title={t(admin ? 'support.adminTitle' : 'support.disabledTitle')}
      subTitle={t(admin ? 'support.adminBody' : 'support.disabledBody')}
      style={{ padding: '8px 0' }}
      extra={[
        <Button type="link" key="relogin" onClick={onRelogin}>
          {t('support.relogin')}
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        message={t(
          registrationConfirmed ? 'support.deviceRegistered' : 'support.deviceUnconfirmed',
        )}
      />
    </Result>
  );
}
